import { Badge, Button, Card, EmptyState, Input, Modal, Select, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import { type AdminOrg, type OrgStatus, useAdminOrgs, useUpdateAdminOrg } from '../../lib/api/admin'

export const Route = createFileRoute('/admin/orgs')({
  component: AdminOrgsPage,
})

const STATUS_OPTIONS: readonly { value: '' | OrgStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'suspended', label: 'Suspended' },
] as const

const PAGE_SIZE = 25

function badgeFor(status: OrgStatus) {
  switch (status) {
    case 'active':
      return <Badge variant="green">active</Badge>
    case 'trial':
      return <Badge variant="blue">trial</Badge>
    case 'suspended':
      return <Badge variant="amber">suspended</Badge>
  }
}

function AdminOrgsPage() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<'' | OrgStatus>('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<AdminOrg | null>(null)
  const [draftStatus, setDraftStatus] = useState<OrgStatus>('active')

  const filters = {
    page,
    page_size: PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(search ? { q: search } : {}),
  }
  const orgs = useAdminOrgs(filters)
  const update = useUpdateAdminOrg()
  const toast = useToast()

  const items = orgs.data?.items ?? []
  const total = orgs.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const openEdit = (org: AdminOrg) => {
    setEditing(org)
    setDraftStatus(org.status)
  }

  const submit = () => {
    if (!editing) return
    update.mutate(
      { id: editing.id, status: draftStatus },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', title: `Updated ${editing.name}` })
          setEditing(null)
        },
        onError: (err) => {
          toast.show({
            severity: 'error',
            title: 'Could not update org',
            description: err instanceof Error ? err.message : 'Unknown error',
          })
        },
      },
    )
  }

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-orgs-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1 id="admin-orgs-title" className="text-[22px] font-semibold tracking-tight m-0">
            Organizations
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Every tenant on the platform. Use Suspend to lock everyone in an org out without
            destroying data.
          </p>
        </header>

        <AdminNav />

        <Card>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              setPage(1)
            }}
          >
            <label className="flex flex-col gap-1 text-[13px] flex-1 min-w-[180px]">
              <span className="font-medium">Search</span>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="name, slug, or billing email"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px] min-w-[160px]">
              <span className="font-medium">Status</span>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as '' | OrgStatus)
                  setPage(1)
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </label>
            <Button type="submit" variant="ghost">
              Apply
            </Button>
          </form>
        </Card>

        <Card dense>
          {orgs.isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading organizations...
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No organizations match"
              description="Try clearing the filters or widening the search."
            />
          ) : (
            <div className="overflow-x-auto -mx-3 -my-3">
              <table
                className="w-full text-[13px] border-separate"
                style={{ borderSpacing: 0 }}
                aria-label="Organizations"
              >
                <thead
                  className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
                  style={{ background: 'var(--bg-3)' }}
                >
                  <tr>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Slug</th>
                    <th className="px-3 py-2 font-medium">Billing email</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((org) => (
                    <tr key={org.id} className="transition-colors hover:bg-[var(--bg-3)]">
                      <td
                        className="px-3 py-2 font-medium border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {org.name}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] text-[var(--text-2)] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {org.slug}
                      </td>
                      <td
                        className="px-3 py-2 break-all text-[var(--text-2)] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {org.billing_email}
                      </td>
                      <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                        {badgeFor(org.status)}
                      </td>
                      <td
                        className="px-3 py-2 text-[var(--text-2)] tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {new Date(org.created_at).toLocaleDateString()}
                      </td>
                      <td
                        className="px-3 py-2 text-right border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <Button size="sm" variant="ghost" onClick={() => openEdit(org)}>
                          Status…
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between text-[12px] text-[var(--text-2)]">
          <span>
            Page {page} of {lastPage} - {total} total
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>

        <Modal
          open={!!editing}
          onClose={() => setEditing(null)}
          title={editing ? `Update ${editing.name}` : ''}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={update.isPending}>
                Cancel
              </Button>
              <Button onClick={submit} loading={update.isPending}>
                Save
              </Button>
            </>
          }
        >
          {editing ? (
            <div className="flex flex-col gap-3">
              <p className="text-[13px] m-0 text-[var(--text-2)]">
                Suspending an org locks every member out. The data remains intact.
              </p>
              <label className="flex flex-col gap-1 text-[13px]">
                <span className="font-medium">Status</span>
                <Select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value as OrgStatus)}
                >
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </label>
            </div>
          ) : null}
        </Modal>
      </section>
    </AdminGuard>
  )
}
